import { cn } from 'common/utils';
import { addImpressionPostEvent } from 'common/utils/post';
import { ScreenName } from 'common/utils/pp_tracking';
import { PinIcon } from 'components/v2/atoms/icon';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useRef, useState } from 'react';
import { PostItemType, PostStatusEnum, PostTypeEnum } from 'service/timeline';

import React from 'react';
import { PostItemContext } from './post-item-context';
import PostTitle from './post-title';
import ReactionBar from './reaction-bar';
import TextContent from './text-content';
import ThreeDots from './three-dots';
import UserInfo from './user-info';

const MediaContent = dynamic(
  () => import('components/v2/modules/post-item/media-content'),
  {
    ssr: false,
  },
);

interface PostItemProps {
  post: PostItemType;
  screen: ScreenName | string;
  isSharedPost?: boolean;
  className?: string;
  isPinned?: boolean;
  isHtmlContent?: boolean;
}

const PostItem = ({
  post,
  screen,
  isSharedPost,
  className,
  isHtmlContent,
}: PostItemProps) => {
  const postRef = useRef<HTMLDivElement>();
  const [hiding, setHiding] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isOpenThreeDots, setIsOpenThreeDots] = useState(false);
  const intersectionCb: IntersectionObserverCallback = useCallback(
    (entries) => {
      const target = entries[0];
      if (target && target.isIntersecting) {
        addImpressionPostEvent({
          postId: post.id,
          screen: screen,
          postSource: post.source,
        });
      }
    },
    [post.id, post.source, screen],
  );

  const onHide = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setHiding(true);
    }, 300);
  };

  useEffect(() => {
    const ref = postRef.current;
    const options = {
      root: null,
      rootMargin: '-45% 0px -45%',
      threshold: 0,
    };
    // Create observer
    const observer = new IntersectionObserver(intersectionCb, options);
    // observe the audio player
    if (ref) {
      observer.observe(ref);
    }
    // clean up on willUnMount
    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [intersectionCb]);
  if (!post) return null;
  if (hiding) return null;
  const isPinnedPost = post.is_pinned && screen === ScreenName.Profile;

  const postContent = (
    <PostItemContext.Provider value={{ post, screen, isSharedPost }}>
      <div
        className={cn(
          'all-font-sans rounded-2xl bg-white shadow-sm transition-all duration-300 ease-in-out hover:shadow-md',
          className ?? '',
          isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100',
          isOpenThreeDots ? 'z-10' : '',
          isPinnedPost ? 'border border-gray-300' : '',
        )}
        ref={postRef}
      >
        <div className="p-4">
          <div className="flex items-center justify-between">
            <UserInfo />
            <ThreeDots
              isOwnPost={post.is_mine}
              isAdsPost={post.is_aff}
              isStreamingPost={!!post.streaming}
              isPinned={post.is_pinned}
              isProfilePage={screen === ScreenName.Profile}
              isGoldPost={post.type === PostTypeEnum.Gold}
              isPostDetail={false}
              isSavedPost={post.is_saved}
              isProcessing={post.status === PostStatusEnum.OnProcessing}
              onDelete={() => {
                onHide();
              }}
              onHide={() => {
                onHide();
              }}
              setIsOpenThreeDots={setIsOpenThreeDots}
              isOpenThreeDots={isOpenThreeDots}
            />
          </div>

          <PostTitle />

          <TextContent isHtmlContent={isHtmlContent} />

          <MediaContent />
        </div>

        {!isSharedPost && <ReactionBar />}
      </div>
    </PostItemContext.Provider>
  );

  if (isPinnedPost) {
    return (
      <div
        className={cn(
          'relative -mx-2 rounded-none bg-pp-pinned-bg px-2 pb-3 pt-9 md:-mx-6 md:px-6',
          isOpenThreeDots ? 'z-10' : '',
        )}
      >
        <div className="absolute left-2 top-2 flex items-center md:left-6">
          <PinIcon className="mr-1 size-6 text-pp-pinned-text" />
          <span className="text-sm font-semibold text-pp-pinned-text">
            固定された投稿
          </span>
        </div>
        {postContent}
      </div>
    );
  }

  return postContent;
};

export default React.memo(PostItem);
